export interface SmsTemplate {
  id: string;
  label: string;
  body: string;
}

export interface VendorSmsConfig {
  version: 1;
  templates: SmsTemplate[];
  assignments: Record<string, string | null>; // key = productRule.id
  defaultTemplateId: string | null;
}

export interface ProductRuleLike {
  id: string;
  source_keyword: string;
  target_name: string;
}

// 하위 호환 fallback용 (기존 VendorConfig.smsTemplates 형태)
export interface LegacySmsTemplateDef {
  label: string;
  body: string;
  productNameIncludes?: string[];
}

const STORAGE_PREFIX = 'vendor-sms-config:';

export function storageKey(vendorCode: string): string {
  return `${STORAGE_PREFIX}${vendorCode}`;
}

export function loadConfig(vendorCode: string): VendorSmsConfig | null {
  try {
    const raw = localStorage.getItem(storageKey(vendorCode));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === 1 && Array.isArray(parsed.templates) && parsed.assignments) {
      return parsed as VendorSmsConfig;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveConfig(vendorCode: string, config: VendorSmsConfig): void {
  localStorage.setItem(storageKey(vendorCode), JSON.stringify(config));
}

export function emptyConfig(): VendorSmsConfig {
  return { version: 1, templates: [], assignments: {}, defaultTemplateId: null };
}

export function newTemplateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `t_${crypto.randomUUID()}`;
  }
  return `t_${Math.random().toString(36).slice(2, 10)}`;
}

function legacyPickSmsBody(templates: LegacySmsTemplateDef[] | undefined, productName: string | undefined): string {
  if (!templates || templates.length === 0) return '';
  if (productName) {
    for (const t of templates) {
      if (t.productNameIncludes && t.productNameIncludes.some(kw => kw && productName.includes(kw))) {
        return t.body;
      }
    }
  }
  const fallback = templates.find(t => !t.productNameIncludes || t.productNameIncludes.length === 0);
  return (fallback ?? templates[0]).body;
}

export function pickSmsBody(
  vendorCode: string,
  productName: string | undefined,
  productRules: ProductRuleLike[],
  legacyTemplates: LegacySmsTemplateDef[] | undefined,
): string {
  const config = loadConfig(vendorCode);
  if (!config) return legacyPickSmsBody(legacyTemplates, productName);

  const rule = productName ? productRules.find(r => r.target_name === productName) : undefined;
  let templateId: string | null = null;
  if (rule) templateId = config.assignments[rule.id] ?? null;
  if (!templateId) templateId = config.defaultTemplateId;
  if (!templateId) return '';

  const t = config.templates.find(x => x.id === templateId);
  return t?.body ?? '';
}
